import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, useTheme } from '@mui/material';
import { PlusIcon, RefreshIcon } from '@sharedCR/coreDesignSystem/icons';
import { getSharedAccordionStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import storage from 'app/services/storage';
import AddButton from 'app/shared/components/common/AddButton';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import Skeleton from 'app/shared/components/common/Skeleton';
import TooltipedSortLabel from 'app/shared/components/common/TooltipedSortLabel';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { ActivityType } from 'constants/activities';
import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { FeatureFlag } from 'constants/featureFlags';
import { ACTIVITIES_FILTER_LS_KEY } from 'constants/localStorage';
import { SKELETON_TYPES } from 'constants/skeleton';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useSort from 'hooks/useSort';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { openDialogAction } from 'store/global/actions';
import { removeActivitiesAction, updateActivitiesAction } from 'store/loanApplications/actions';
import {
  selectApplicationCallContacts,
  selectCallEmployeeIds,
  selectFilteredActivities,
  selectHasContactsForSuggestion,
} from 'store/loanApplications/selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';
import { getUsersAction } from 'store/users/actions';

import { ACTIVITIES_SORT_ID, ActivitiesTab } from '../Activities.constants';
import { getStyles } from '../Activities.styles';
import { ActivitiesTestIds } from '../Activities.testIds';
import useNewEmailModal from '../hooks/useNewEmailModal';

import ActivitiesFilter from './ActivitiesFilter';
import { getAvailableActivityTypes } from './ActivitiesFilter.config';
import { CallActivityItem, CommonActivityItem, EmailActivityItem } from './ActivityItems';
import ContactsModal from './ContactsModal';

const TabToComponentMap = {
  [ActivityType.COMMON_UPDATE]: CommonActivityItem,
  [ActivityType.CONTACT_ACTIVITY]: CommonActivityItem,
  [ActivityType.EMAIL]: EmailActivityItem,
  [ActivityType.CALL]: CallActivityItem,
};

const ActivitiesList = ({
  setIsLoading,
  isLoading,
  activeTab,
  getAbortSignal,
  scrollContainerRef,
}) => {
  const theme = useTheme();
  const sharedClasses = useMemo(() => getSharedAccordionStyles(theme), [theme]);
  const classes = getStyles(theme);

  const rootSx = useMemo(
    () => ({ ...sharedClasses.root, ...sharedClasses.accordion }),
    [sharedClasses.root, sharedClasses.accordion],
  );

  const isLightWeightEmailBodyAllowed = useFeatureToggle(FeatureFlag.EnableLightWeightEmailBody);

  const isManageCallsAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_CALLS),
  );

  const availableActivityTypes = getAvailableActivityTypes(isManageCallsAllowed);

  const { applicationId } = useParams();

  const [isContactsLoading, setIsContactsLoading] = useState(false);

  const dispatch = useDispatch();

  const [isCompanyUsersLoading, setIsCompanyUsersLoading] = useState(false);

  const employeeIds = useSelector(selectCallEmployeeIds);

  useEffectSafeStrictMode(() => {
    if (!Array.isArray(employeeIds) || !employeeIds.length) {
      return;
    }

    setIsCompanyUsersLoading(true);

    dispatch(getUsersAction({ includeDeleted: true, id: employeeIds }))
      .catch(Error)
      .finally(() => setIsCompanyUsersLoading(false));
  }, [employeeIds, dispatch, setIsCompanyUsersLoading]);

  const { order, onRequestSort } = useSort({ direction: 'desc', id: ACTIVITIES_SORT_ID });

  const { filterFromLS, onFilterChange } = useMemo(
    () => ({
      filterFromLS: storage.getParsedItem(ACTIVITIES_FILTER_LS_KEY) || availableActivityTypes,
      onFilterChange: filter => storage.setItem(ACTIVITIES_FILTER_LS_KEY, filter, true),
    }),
    [availableActivityTypes],
  );

  const [filteredTypes, setFilteredTypesRaw] = useState(filterFromLS);

  const setFilteredTypes = useCallback(
    filter => {
      setFilteredTypesRaw(filter);
      onFilterChange(filter);
    },
    [onFilterChange, setFilteredTypesRaw],
  );

  const activities = useSelector(selectFilteredActivities);
  const activitiesDebouncedSearchValue = useSelector(getDebouncedSearchValue);
  const hasContactsForSuggestion = useSelector(selectHasContactsForSuggestion);
  const callContacts = useSelector(selectApplicationCallContacts);

  const isAllTabActive = useMemo(() => activeTab === ActivitiesTab.ALL, [activeTab]);

  const orderedActivities = useMemo(() => {
    const { id, direction } = order;

    return _.orderBy(activities, [activity => new Date(activity[id])], [direction]);
  }, [activities, order]);

  const updateActivities = useCallback(() => {
    const isActivitesSearchAllowed =
      isLightWeightEmailBodyAllowed && activitiesDebouncedSearchValue;

    setIsLoading(true);

    dispatch(
      updateActivitiesAction({
        applicationId,
        isAllTabActive,
        filteredTypes,
        applicationContactsCallback: () => setIsContactsLoading(false),
        activeTab,
        isManageCallsAllowed,
        signal: getAbortSignal?.(),
        ...(isActivitesSearchAllowed ? { searchTerm: activitiesDebouncedSearchValue } : {}),
      }),
    ).finally(() => setIsLoading(false));
  }, [
    activeTab,
    applicationId,
    dispatch,
    isAllTabActive,
    getAbortSignal,
    isManageCallsAllowed,
    filteredTypes,
    setIsLoading,
    activitiesDebouncedSearchValue,
    isLightWeightEmailBodyAllowed,
  ]);

  useEffectSafeStrictMode(() => {
    if (!applicationId) {
      return;
    }

    dispatch(removeActivitiesAction());

    updateActivities();
  }, [applicationId, dispatch, filteredTypes, updateActivities, activeTab]);

  const { onCreateEmail } = useNewEmailModal();

  const isFoundActivities = !isLoading && !!activities.length;
  const isNoActivitiesFound = !isLoading && !activities.length;
  const isEmailsActiveTab = activeTab === ActivitiesTab.EMAILS;
  const isCallsActiveTab = activeTab === ActivitiesTab.CALLS;

  const isNewEmailButtonShown = isEmailsActiveTab || isAllTabActive;

  const onOpenContactsModal = useCallback(() => {
    dispatch(
      openDialogAction({
        header: 'Contacts',
        size: ModalSize.XL,
        body: dialogProps => <ContactsModal {...dialogProps} />,
      }),
    );
  }, [dispatch]);

  return (
    <Box
      className="flex flex-1 min-w-0 flex-col w-full p-16 relative"
      ref={scrollContainerRef}
      sx={rootSx}
    >
      {isNewEmailButtonShown && (
        <AddButton
          ariaLabel="Create email"
          dataTestId={ActivitiesTestIds.CREATE_EMAIL_BUTTON}
          disabled={isContactsLoading || !hasContactsForSuggestion}
          icon={PlusIcon}
          styleConfig="absolute"
          sx={classes.addButton}
          onClick={onCreateEmail}
        />
      )}
      {isCallsActiveTab && (
        <AddButton
          ariaLabel="Show all contacts"
          dataTestId={ActivitiesTestIds.SHOW_ALL_CONTACTS_BUTTON}
          disabled={!callContacts.length}
          icon={PlusIcon}
          styleConfig="absolute"
          sx={classes.addButton}
          onClick={onOpenContactsModal}
        />
      )}
      <div className="flex justify-end items-baseline m-16">
        {isAllTabActive && (
          <ActivitiesFilter
            availableActivityTypes={availableActivityTypes}
            filteredTypes={filteredTypes}
            isLoading={isLoading}
            setFilteredTypes={setFilteredTypes}
          />
        )}
        <div className="flex flex-none items-center">
          <ButtonWithTooltip
            className="mr-5"
            data-testid={ActivitiesTestIds.REFRESH_BUTTON}
            icon={RefreshIcon}
            title="Refresh"
            onClick={updateActivities}
          />
          <TooltipedSortLabel
            isSort
            align="left"
            id={ACTIVITIES_SORT_ID}
            label="By date"
            order={order}
            onRequestSort={onRequestSort}
          />
        </div>
      </div>
      {isLoading && <Skeleton type={SKELETON_TYPES.LIST} />}
      {isFoundActivities &&
        orderedActivities.map(activity => {
          const Component = TabToComponentMap[activity.type];
          const key =
            activity.threadId ||
            activity.callHistoryId ||
            `${activity.type}-${activity.activityDateTime}`;

          return (
            <Component
              activity={activity}
              applicationId={applicationId}
              isCompanyUsersLoading={isCompanyUsersLoading}
              key={key}
            />
          );
        })}
      {isNoActivitiesFound && (
        <NoAvailableMessage message="Sorry, but we could not find any activity on this state" />
      )}
    </Box>
  );
};

ActivitiesList.propTypes = {
  ...WithLoadingPropTypes,
  getAbortSignal: PropTypes.func,
};

export default compose(withLoading, memo)(ActivitiesList);
